import {
  Avatar,
  AvatarFallback,
  Badge,
  Heading,
  Icon,
  TextField,
} from "@elaniintc/goes-design-system";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import FieldCard from "../entity/components/FieldCard";
import { getFieldTypeInfo } from "../utils";
import { UsersList } from "./components/UsersList";
import { IdField } from "./components/IdField";
import { RegistryFormField } from "./components/RegistryFormField";
import { RegistryFormProvider } from "./contexts/RegistryFormContext";
import { RegistryList } from "./components/RegistryList";
import { ConfirmationModalBase } from "@/components/common/modals";
import type { EntityRegistryPageContentProps } from "./index.types";
import { useEntityRegistryForm } from "./use-entity-registry-form";

export const EntityRegistryPage = () => {
  const { id, registryId } = useParams();
  const navigate = useNavigate();
  const [openRegistryChangesModal, setOpenRegistryChangesModal] =
    useState(false);

  const handleCancel = () => {
    setOpenRegistryChangesModal(true);
  };

  const confirmExit = () => {
    setOpenRegistryChangesModal(false);
    let link = "";
    if (id && registryId) {
      link = `/entidad/${id}/detalle-registro/${registryId}`;
    } else {
      link = id ? `?entity=${id}` : "";
    }
    navigate(`/expedientes${link}`);
  };

  return (
    <>
      <ConfirmationModalBase
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        isOpen={openRegistryChangesModal}
        onAccept={confirmExit}
        onClose={() => setOpenRegistryChangesModal(false)}
        acceptVariant="danger-secondary"
      />
      <RegistryFormProvider>
        <EntityRegistryPageContent onCancel={handleCancel} />
      </RegistryFormProvider>
    </>
  );
};

const EntityRegistryPageContent = ({
  onCancel,
}: EntityRegistryPageContentProps) => {
  const {
    isLoading,
    isLoadingRegistry,
    regName,
    setRegName,
    registryName,
    users,
    sections,
    registryData,
    regId,
    setRegId,
    entityId,
    registryId,
    fieldOptions,
    fieldValues,
    handleFieldChange,
    handleOptionChange,
  } = useEntityRegistryForm(onCancel);

  if (isLoading || isLoadingRegistry) {
    return (
      <div className="flex justify-center mx-auto pt-16">
        <Icon
          name="spinner-third"
          size="lg"
          className="animate-spin text-sm text-on-disabled"
          type="far"
        />
      </div>
    );
  }
  let regisName = regName || "";
  if (!regisName && registryName) {
    regisName = "Nombre de " + registryName;
  }
  return (
    <div className="flex flex-col md:flex-row gap-3 p-8">
      <div className="w-full md:w-1/2">
        <div className="p-4 flex flex-col gap-4">
          <Avatar>
            <AvatarFallback color="neutral">
              <Icon type="fas" name="folder" />
            </AvatarFallback>
          </Avatar>
          <Heading variant="h5" color="loud">
            {regisName}
          </Heading>
          <div>
            {users.length === 0 ? (
              <p className="text-body-quiet">Sin usuarios asociados</p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {users.map((user) => (
                  <Badge
                    key={user.applicant_id}
                    variant={user.is_owner ? "success" : "action"}
                    size="sm"
                  >
                    <Icon
                      type="fas"
                      name={user.is_owner ? "user-shield" : "user"}
                      size="xs"
                      className={
                        user.is_owner
                          ? "text-success-quiet"
                          : "text-action-quiet"
                      }
                    />
                    {user.applicant?.email || ""}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <hr className="border-neutral-quiet" />
          {sections.map((section) => (
            <div key={section.id} className="flex flex-col">
              <p className="text-body-loud text-size-body leading-3xl font-semibold">
                {section.title}
              </p>

              {section.fields.map((field) => {
                const fieldInfo = getFieldTypeInfo(field);
                return (
                  <FieldCard
                    className="border-none"
                    key={field.id}
                    field={field}
                    fieldInfo={fieldInfo}
                    sectionId={section.id}
                    readonly
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div className=" w-full md:w-1/2 bg-neutral-object border border-neutral-quiet rounded-lg p-6">
        <div className="flex flex-col gap-4">
          <TextField
            label={
              registryName ? "Nombre de " + registryName : "Nombre del registro"
            }
            required
            placeholder={
              "Ingrese el nombre " +
              (registryName ? "de " + registryName : "del registro")
            }
            value={regName}
            onChange={(e) => setRegName(e.target.value)}
          />
          <IdField
            label="ID"
            required
            canUnlock
            originalValue={registryData?.unique_code}
            value={regId}
            onChange={(value) => setRegId(value)}
            entityId={entityId}
            currentRegistryId={registryId}
          />

          <UsersList />
          <RegistryList
            registryCode={registryData?.unique_code}
            registries={registryData?.related_registries ?? undefined}
          />
          <hr className="border-neutral-quiet" />
          {sections.map((section, index) => (
            <div key={section.id} className="flex flex-col gap-2">
              <p className="text-body-loud text-size-body-lg font-semibold">
                {section.title}
              </p>
              {section.fields.map((field) => {
                const fieldOption = fieldOptions[field.id];
                const optionVal =
                  typeof fieldOption === "string"
                    ? fieldOption
                    : fieldOption?.id || "";
                return (
                  <RegistryFormField
                    key={field.id}
                    field={field}
                    value={fieldValues[field.id]}
                    optionValue={optionVal}
                    onChange={(value) => handleFieldChange(field.id, value)}
                    onOptionChange={(
                      fieldId,
                      option,
                      label,
                      table,
                      isDataCenter,
                    ) =>
                      handleOptionChange(
                        fieldId,
                        option,
                        label,
                        table,
                        isDataCenter,
                      )
                    }
                  />
                );
              })}
              {index < sections.length - 1 && (
                <hr className="border-neutral-quiet mt-4" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
